import { motion } from 'framer-motion'
import { ArrowRight, Coffee } from 'lucide-react'

const Hero = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#kapcsolat')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Coffee bean shapes */}
        <motion.div
          className="absolute top-32 right-10 w-20 h-32 bg-coffee-200/30 rounded-full blur-2xl"
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-32 left-10 w-32 h-20 bg-coffee-300/20 rounded-full blur-3xl"
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 right-1/4 w-16 h-24 bg-coffee-200/20 rounded-full blur-xl"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="lg:col-span-6 xl:col-span-5 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-2"
            >
              <span className="inline-flex items-center gap-2 text-coffee-600 text-sm font-medium tracking-widest uppercase">
                <Coffee size={16} className="animate-steam" />
                KV Studio
              </span>
            </motion.div>

            <motion.h1
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-coffee-900 leading-[1.05] tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              Marketing, ami{' '}
              <span className="relative inline-block">
                <span className="relative z-10">felébreszti</span>
                <motion.span
                  className="absolute bottom-2 left-0 right-0 h-3 bg-coffee-200/60 -z-10"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                />
              </span>{' '}
              a vállalkozásod
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-coffee-700 leading-relaxed max-w-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Weboldalak, hirdetések és SEO – hogy végre jöjjenek az ügyfelek. 
              Mi adjuk a marketinged koffeinjét.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <button
                onClick={scrollToContact}
                className="group inline-flex items-center justify-center gap-2 bg-coffee-900 text-cream px-8 py-4 rounded-full font-semibold text-base hover:bg-coffee-800 transition-all duration-300 btn-primary shadow-lg shadow-coffee-900/20"
                data-testid="hero-cta-primary"
              >
                Kérj ajánlatot
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <button
                onClick={() => {
                  const el = document.querySelector('#szolgaltatasok')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                className="inline-flex items-center justify-center gap-2 bg-coffee-100 text-coffee-800 px-8 py-4 rounded-full font-semibold text-base hover:bg-coffee-200 transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Szolgáltatások
              </button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              className="pt-8 border-t border-coffee-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <p className="text-sm text-coffee-500 mb-3 font-medium tracking-wide uppercase">
                Ingyenes konzultáció
              </p>
              <p className="text-coffee-700 font-accent text-xl">
                "30 perces beszélgetés, ami megváltoztathatja a vállalkozásod"
              </p>
            </motion.div>
          </div>

          {/* Right Content - Image */}
          <motion.div
            className="lg:col-span-6 xl:col-span-7 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-coffee-900/10">
                <img
                  src="https://images.unsplash.com/photo-1718279602896-6df6c34f61e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Modern munkakörnyezet"
                  className="w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-coffee-900/20 to-transparent" />
              </div>

              {/* Floating card */}
              <motion.div
                className="absolute -bottom-6 -left-6 md:bottom-8 md:left-8 bg-white p-6 rounded-2xl shadow-xl max-w-[280px]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-coffee-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">☕</span>
                  </div>
                  <div>
                    <p className="font-semibold text-coffee-900">+150%</p>
                    <p className="text-sm text-coffee-600">Látogatottság növekedés</p>
                  </div>
                </div>
              </motion.div>

              {/* Decorative element */}
              <motion.div
                className="absolute -top-4 -right-4 md:top-8 md:right-8 w-20 h-20 bg-coffee-600 rounded-full flex items-center justify-center text-white shadow-lg"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <span className="font-accent text-lg text-center leading-tight">
                  Ingyenes<br/>konzultáció
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-coffee-400 rounded-full flex justify-center p-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <motion.div
            className="w-1.5 h-1.5 bg-coffee-600 rounded-full"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero
